import { $, $$, on, doc } from './util.js';
import { readSettings, writeSettings } from './settings.js';

/* REFERENCE — Developer Docs › Reference pages
   --------------------------------------------------------------------------
   Markup: templates/documentation/global-pages/{model,routes,templates,class}.html
   and templates/documentation/includes/reference/*. Styles: scss/components/
   _reference.scss.

   Views. The platform's abstractions Frontend, Api and Backend are shown to
   readers as Templates, API & Jobs and Admin. A model page carries all three
   in its HTML; which one shows is CSS keyed off html[data-ref-view], set
   before first paint by the inline script in base-template.html (saved
   setting, or a #templates / #api / #admin hash, or a member anchor such as
   #admin-getId). This module only changes that attribute, saves the choice in
   the shared settings entry (settings.js) and keeps ARIA in step.

   Routes and templates pages have one URL per view, so their switcher is
   links; following one saves the choice too. Every link to a routes or
   templates page that carries data-ref-base / data-ref-views is pointed at the
   saved view when that view has content.

   Also: the member filter on model pages, the sidebar filter and its "/"
   shortcut, module names that fold their branch, copy buttons, and the
   "+N more" button on a model's "Also in" list. */

var VIEWS = ['Frontend', 'Api', 'Backend'];
var SUFFIX = { Frontend: '', Api: '/api', Backend: '/admin' };
var HASH_VIEWS = { templates: 'Frontend', api: 'Api', admin: 'Backend' };

function currentView() {
	var view = doc.documentElement.getAttribute('data-ref-view');
	return VIEWS.indexOf(view) === -1 ? 'Frontend' : view;
}

function savedView() {
	var view = readSettings().referenceView;
	return VIEWS.indexOf(view) === -1 ? null : view;
}

function viewFromHash() {
	var token = (window.location.hash || '').slice(1).split('-')[0];
	return HASH_VIEWS[token] || null;
}

/* Links to routes and templates pages follow the saved view, falling back to
   the first view the module has content in (the link's server-side href). */
function pointViewLinks() {
	var preferred = savedView();
	if (!preferred) { return; }
	$$('[data-ref-base]').forEach(function (link) {
		var views = (link.getAttribute('data-ref-views') || '').split(',');
		if (views.indexOf(preferred) === -1) { return; }
		link.setAttribute('href', link.getAttribute('data-ref-base') + SUFFIX[preferred]);
	});
}

function initViews(page) {
	// Links mode: the reader is choosing a view; remember it.
	$$('nav.ref-sw a.ref-seg', page).forEach(function (link) {
		on(link, 'click', function () { writeSettings({ referenceView: link.getAttribute('data-ab') }); });
	});

	if (!page.classList.contains('ref-multi')) { return; }
	var tabs = $$('.ref-sw [role="tab"]', page);
	if (!tabs.length) { return; }
	var available = tabs.map(function (tab) { return tab.getAttribute('data-ab'); });

	function sync() {
		var view = currentView();
		tabs.forEach(function (tab) {
			var selected = tab.getAttribute('data-ab') === view;
			tab.setAttribute('aria-selected', selected ? 'true' : 'false');
			tab.tabIndex = selected ? 0 : -1;
		});
	}

	function show(view, save) {
		if (available.indexOf(view) === -1) { return; }
		doc.documentElement.setAttribute('data-ref-view', view);
		if (save) {
			writeSettings({ referenceView: view });
			pointViewLinks();
		}
		sync();
		page.dispatchEvent(new CustomEvent('ref:view'));
	}

	if (available.indexOf(currentView()) === -1) { show(available[0], false); }
	sync();

	tabs.forEach(function (tab, i) {
		on(tab, 'click', function () { show(tab.getAttribute('data-ab'), true); });
		on(tab, 'keydown', function (e) {
			var step = e.key === 'ArrowRight' ? 1 : (e.key === 'ArrowLeft' ? -1 : 0);
			if (!step) { return; }
			e.preventDefault();
			var next = tabs[(i + step + tabs.length) % tabs.length];
			next.focus();
			show(next.getAttribute('data-ab'), true);
		});
	});

	// A link to #api or #admin-getId on the same page: show that view, for
	// this visit only, as the pre-paint script does on a fresh load.
	on(window, 'hashchange', function () {
		var view = viewFromHash();
		if (view) { show(view, false); }
	});
}

function initMemberFilter(page) {
	var input = $('#refMemberFilter', page);
	if (!input) { return; }
	var count = $('#refMemberCount', page);

	function apply() {
		var query = input.value.trim().toLowerCase();
		var active = $('.ref-panel[data-view="' + currentView() + '"]', page);
		var shown = 0;
		var total = 0;
		$$('.ref-panel', page).forEach(function (panel) {
			var matches = 0;
			$$('.ref-mem', panel).forEach(function (member) {
				var hit = !query || member.getAttribute('data-name').indexOf(query) !== -1;
				member.hidden = !hit;
				if (hit) { matches++; }
				if (panel === active) {
					total++;
					if (hit) { shown++; }
				}
			});
			$$('.ref-grp', panel).forEach(function (group) {
				group.hidden = !$$('.ref-mem', group).some(function (member) { return !member.hidden; });
			});
			var none = $('.ref-nores', panel);
			if (none) { none.hidden = matches > 0; }
		});
		if (count) { count.textContent = query ? shown + ' of ' + total + ' shown' : ''; }
	}

	on(input, 'input', apply);
	on(input, 'keydown', function (e) {
		if (e.key === 'Escape' && input.value) { input.value = ''; apply(); }
	});
	on(page, 'ref:view', apply);
}

function initTree() {
	var tree = $('#refTree');
	if (!tree) { return; }

	// A module's name folds its branch, like the chevron beside it.
	$$('[data-ref-toggle]', tree).forEach(function (button) {
		on(button, 'click', function () {
			var toggle = button.parentNode.querySelector('.sb-toggle');
			if (toggle) { toggle.click(); }
		});
	});

	var input = $('#refFilter');
	if (!input) { return; }
	var empty = $('.ref-filter-empty');
	var lists = $$('ul[id]', tree);
	var initial = null;

	function toggleFor(list) {
		return tree.querySelector('[aria-controls="' + list.id + '"]');
	}

	function setOpen(list, open) {
		list.hidden = !open;
		var toggle = toggleFor(list);
		if (toggle) { toggle.setAttribute('aria-expanded', open ? 'true' : 'false'); }
	}

	function restore() {
		if (initial) { lists.forEach(function (list, i) { setOpen(list, initial[i]); }); }
		initial = null;
		$$('li', tree).forEach(function (item) { item.hidden = false; });
		if (empty) { empty.hidden = true; }
	}

	function apply() {
		var query = input.value.trim().toLowerCase();
		if (!query) { restore(); return; }
		// Remember which branches the reader had open, to put them back.
		if (!initial) { initial = lists.map(function (list) { return !list.hidden; }); }

		var anySection = false;
		$$('.ref-sec', tree).forEach(function (section) {
			var level1 = $('.ref-l1', section);
			var sectionHit = false;
			Array.prototype.forEach.call(level1.children, function (item) {
				var level2 = $('.ref-l2', item);
				if (level2) {
					var moduleHit = (item.getAttribute('data-ref-mod') || '').indexOf(query) !== -1;
					var anyModel = false;
					Array.prototype.forEach.call(level2.children, function (model) {
						var hit = moduleHit || (model.getAttribute('data-ref-name') || '').indexOf(query) !== -1;
						model.hidden = !hit;
						if (hit) { anyModel = true; }
					});
					item.hidden = !anyModel;
					setOpen(level2, anyModel);
					if (anyModel) { sectionHit = true; }
				} else {
					var key = item.getAttribute('data-ref-mod') || item.getAttribute('data-ref-name') || '';
					var hit = key.indexOf(query) !== -1;
					item.hidden = !hit;
					if (hit) { sectionHit = true; }
				}
			});
			section.hidden = !sectionHit;
			setOpen(level1, sectionHit);
			if (sectionHit) { anySection = true; }
		});
		if (empty) { empty.hidden = anySection; }
	}

	on(input, 'input', apply);
	on(input, 'keydown', function (e) {
		if (e.key === 'Escape' && input.value) { input.value = ''; apply(); }
	});

	// "/" jumps to the filter, unless the reader is already typing somewhere.
	on(doc, 'keydown', function (e) {
		if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey) { return; }
		var target = e.target;
		var tag = target && target.tagName;
		if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (target && target.isContentEditable)) { return; }
		if (!input.offsetParent) { return; }
		e.preventDefault();
		input.focus();
	});
}

function initCopy(page) {
	$$('[data-ref-copy]', page).forEach(function (button) {
		on(button, 'click', function () {
			var source = doc.getElementById(button.getAttribute('data-ref-copy'));
			if (!source) { return; }
			var text = source.textContent.trim();
			var label = button.textContent;
			function done() {
				button.textContent = 'Copied';
				setTimeout(function () { button.textContent = label; }, 1400);
			}
			function selectInstead() {
				var range = doc.createRange();
				range.selectNodeContents(source);
				var selection = window.getSelection();
				selection.removeAllRanges();
				selection.addRange(range);
			}
			if (navigator.clipboard && navigator.clipboard.writeText) {
				navigator.clipboard.writeText(text).then(done, selectInstead);
			} else {
				selectInstead();
			}
		});
	});
}

/* A model's "Also in" list: the names past the first six carry
   data-ref-extra and are all in the HTML; hide them behind the button,
   which the template ships hidden so it shows only when this runs. */
function initSiblings(page) {
	$$('[data-ref-more]', page).forEach(function (button) {
		var extras = $$('[data-ref-extra]', button.parentNode);
		var label = button.textContent;
		function set(open) {
			extras.forEach(function (link) { link.hidden = !open; });
			button.textContent = open ? 'Show fewer' : label;
			button.setAttribute('aria-expanded', open ? 'true' : 'false');
		}
		set(false);
		button.hidden = false;
		on(button, 'click', function () { set(button.getAttribute('aria-expanded') !== 'true'); });
	});
}

export function initReference() {
	var page = $('.ref-page');
	if (!page) { return; }
	initViews(page);
	pointViewLinks();
	initMemberFilter(page);
	initTree();
	initCopy(page);
	initSiblings(page);
}
